import { BrokerInactiveNotice } from '@/features/brokers/components/broker-inactive-notice.component';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { getProductConfig } from '@/app/config/products.config';
import { resolveCapitalGlobalQuoteWizardRedirect } from '@/features/quotes/components/capital-global/capital-global-quote-product.util';
import { BrokerProductCategory } from '@/services/brokers/types';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { Page, PageHeader } from '@/shared/ui/page';
import { toaster } from '@/shared/ui/toaster.component';
import { isClonedQuoteMarkedAsDraft } from '@/shared/utils/clone-quote-status.util';
import { Button, Spinner } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { Copy, Home } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useQuoteParams } from '../../context/quote-params.context';
import { QuoteSummaryProvider } from '../../context/quote-summary.context';
import { useCopyQuote } from '../../hooks/common/use-copy-quote.hook';
import { useProposalDetails } from '../../hooks/homeowner/use-proposal-details.hook';
import { parseProductCode } from '../../utils/parse-product-code';
import { CloneQuoteDialog } from '../clone-quote.dialog';
import { EditQuoteForm } from '../edit-quote-form.component';

type Props = {
  policyId: string;
  mode: 'edit' | 'view';
  category?: BrokerProductCategory;
};

export function EditQuoteLayout({ policyId, mode, category }: Props) {
  const { setParams } = useQuoteParams();
  const [isOnLastStep, setIsOnLastStep] = useState(false);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const copyQuoteMutation = useCopyQuote();
  const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
  const navigate = useNavigate();
  const {
    data: proposalDetails,
    isLoading,
    error,
  } = useProposalDetails(policyId);

  const proposalCategory: BrokerProductCategory =
    category || parseProductCode(proposalDetails?.productCode ?? '');

  const editProductConfig = proposalDetails
    ? getProductConfig(proposalDetails.productCode)
    : undefined;

  const shouldRedirectToCapitalGlobalWizard =
    mode === 'edit' &&
    proposalDetails !== undefined &&
    editProductConfig?.usesCapitalGlobalProposal === true;

  // Group Life PME tem wizard CQRS próprio — tanto editar quanto visualizar
  // redirecionam para `/quotes/products/new/$id` (que hidrata via
  // `useHydrateGroupLifeQuote`). O EditQuoteForm comum não suporta o produto.
  const shouldRedirectToGroupLifeWizard =
    proposalDetails !== undefined &&
    editProductConfig?.usesGroupLifeProposal === true;

  useEffect(() => {
    if (!proposalDetails) return;
    const cfg = getProductConfig(proposalDetails.productCode);
    if (cfg?.usesCapitalGlobalProposal && mode === 'edit') {
      const { stepCode, quoteFlow } = resolveCapitalGlobalQuoteWizardRedirect({
        statusPortal: proposalDetails.statusPortal,
        mode: 'edit',
      });

      void navigate({
        to: '/quotes/products/new/$id',
        params: { id: proposalDetails.policyId },
        search: {
          productId: String(proposalDetails.productId),
          category: cfg.schemaCategory,
          productCode: cfg.productCode,
          stepCode,
          quoteFlow,
        },
        replace: true,
      });
      return;
    }
    if (cfg?.usesGroupLifeProposal) {
      void navigate({
        to: '/quotes/products/new/$id',
        params: { id: proposalDetails.policyId },
        search: {
          productId: String(proposalDetails.productId),
          category: cfg.schemaCategory,
          productCode: cfg.productCode,
          stepCode: '',
          quoteFlow: 'edit',
        },
        replace: true,
      });
    }
  }, [proposalDetails, mode, navigate]);

  useEffect(() => {
    if (proposalDetails) {
      setParams({
        policyId: proposalDetails.policyId,
        proposalNumber:
          proposalDetails.quotationNo || proposalDetails.proposalNo,
        id: proposalDetails.policyId,
        productId: String(proposalDetails.productId),
        category: proposalCategory,
        productCode: proposalDetails.productCode,
      });
    }
  }, [proposalDetails, proposalCategory, setParams]);

  const handleCloseCloneDialog = () => {
    setIsCloneDialogOpen(false);
    copyQuoteMutation.reset();
  };

  const handleConfirmClone = async () => {
    if (!proposalDetails?.policyId) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível clonar',
        description: 'ID da cotação (PolicyId) não encontrado.',
      });
      handleCloseCloneDialog();
      return;
    }

    try {
      const { policyId: newPolicyId } = await copyQuoteMutation.mutateAsync({
        policyId: proposalDetails.policyId,
        quoteNumber: proposalDetails.quotationNo || proposalDetails.proposalNo,
      });

      toaster.create({
        type: 'success',
        title: 'Cotação clonada com sucesso',
      });

      handleCloseCloneDialog();
      navigate({
        to: '/quotes/$policyId/edit',
        params: { policyId: newPolicyId },
        search: { mode: 'edit' },
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Erro ao clonar cotação.';

      toaster.create({
        type: 'error',
        title: 'Não foi possível clonar',
        description: message.includes('Deductible')
          ? `${message} (edite a cotação para ajustar franquias e tente clonar novamente)`
          : message,
      });
    }
  };

  if (isLoading) {
    return (
      <Page.Root className="h-auto lg:h-full">
        <div className='flex h-[50vh] items-center justify-center'>
          <div className='flex flex-col items-center gap-4'>
            <Spinner size='lg' />
            <span className='text-alba-gray-600'>
              Carregando detalhes da cotação...
            </span>
          </div>
        </div>
      </Page.Root>
    );
  }

  if (error || !proposalDetails) {
    return (
      <Page.Root className="h-auto lg:h-full">
        <div className='flex h-[50vh] items-center justify-center'>
          <div className='flex flex-col items-center gap-4 text-center'>
            <span className='text-lg font-bold text-alba-error-red-500'>
              Erro ao carregar cotação
            </span>
            <span className='text-alba-gray-600'>
              {error?.message || 'Proposta não encontrada'}
            </span>
          </div>
        </div>
      </Page.Root>
    );
  }

  if (shouldRedirectToCapitalGlobalWizard || shouldRedirectToGroupLifeWizard) {
    return (
      <Page.Root className="h-auto lg:h-full">
        <div className='flex h-[50vh] items-center justify-center'>
          <div className='flex flex-col items-center gap-4'>
            <Spinner size='lg' />
            <span className='text-alba-gray-600'>
              Carregando detalhes da cotação...
            </span>
          </div>
        </div>
      </Page.Root>
    );
  }

  const isViewMode = mode === 'view';
  const isClonedDraft = isClonedQuoteMarkedAsDraft(proposalDetails.policyId);

  const pageTitle = isViewMode
    ? 'Detalhes da Cotação'
    : isClonedDraft
      ? `Nova Cotação - ${proposalCategory === 'BUSINESS_INSURANCE' ? 'Empresarial' : 'Residencial'}`
      : 'Editar Cotação';
  const proposalNoLabel = (
    proposalDetails.quotationNo || proposalDetails.proposalNo
  )?.trim();
  const pageSubtitle = isViewMode
    ? 'Visualize as informações da cotação'
    : isClonedDraft
      ? 'Preencha as informações para calcular o seguro'
      : 'Edite as informações da cotação';

  const portalStatus = Number(proposalDetails.statusPortal) || 0;
  const isTerminalStatus =
    portalStatus === PortalStatus.QuoteClosed ||
    portalStatus === PortalStatus.QuoteExpired ||
    portalStatus === PortalStatus.QuoteDeleted;
  const resolvedPortalStatus =
    !isTerminalStatus && isClonedDraft
      ? PortalStatus.QuoteInProgress
      : portalStatus;
  const canClone =
    resolvedPortalStatus === PortalStatus.QuoteInProgress ||
    resolvedPortalStatus === PortalStatus.QuoteCalculated;
  const showCloneAction = isOnLastStep && canClone;

  return (
    <QuoteSummaryProvider>
      <Page.Root className="h-auto lg:h-full">
        <Page.Breadcrumb
          items={[
            {
              label: 'Página Inicial',
              href: '/dashboard',
              iconLeft: <Home className='h-4 w-4' />,
            },
            { label: 'Cotações', href: '/quotes' },
            ...(proposalNoLabel
              ? [{ label: proposalNoLabel, active: true }]
              : [{ label: 'Cotação', active: true }]),
          ]}
        />

        <PageHeader.Root>
          <PageHeader.Main>
            <PageHeader.TitleGroup>
              <PageHeader.Title>{pageTitle}</PageHeader.Title>
              <PageHeader.Subtitle>{pageSubtitle}</PageHeader.Subtitle>
            </PageHeader.TitleGroup>
            {showCloneAction && (
              <PageHeader.Actions>
                <div
                  title={quoteWriteGuard.reason}
                  className='flex flex-col items-end gap-1'
                >
                  <Button
                    variant='ghost'
                    size='large'
                    loading={copyQuoteMutation.isPending}
                    disabled={
                      copyQuoteMutation.isPending || quoteWriteGuard.blocked
                    }
                    onClick={() => setIsCloneDialogOpen(true)}
                  >
                    <Copy className='h-5 w-5' strokeWidth={1.6} />
                    Clonar
                  </Button>
                </div>
              </PageHeader.Actions>
            )}
          </PageHeader.Main>
        </PageHeader.Root>

        <BrokerInactiveNotice guards={[quoteWriteGuard]} />

        <Page.Content className='py-4'>
          <EditQuoteForm
            proposalDetails={proposalDetails}
            mode={mode}
            onLastStepChange={setIsOnLastStep}
          />
        </Page.Content>

        <CloneQuoteDialog
          isOpen={isCloneDialogOpen}
          isLoading={copyQuoteMutation.isPending}
          onClose={handleCloseCloneDialog}
          onConfirm={handleConfirmClone}
        />
      </Page.Root>
    </QuoteSummaryProvider>
  );
}
